import { useCallback, useEffect, useRef, useState } from "react";
import io, { Socket } from "socket.io-client";
import { dmnIdentity } from "../utils/dmnIdentity";
import { getEnvironmentProps } from "../utils/environment";

export type customMessageEvent<T = any> = {
  channel?: string;
  message: T;
};
type UseWebSocketOptions = {
  channel: string;
  onMessage: (e: customMessageEvent) => void;
  onDisconnect?: () => void;
};
export const useWebSocket = ({
  channel,
  onMessage,
  onDisconnect
}: UseWebSocketOptions) => {
  const [session, setSession] = useState<Socket | null>(null);
  const previousChannel = useRef<string>(channel);

  useEffect(() => {
    connect();
    return () => {
      previousChannel.current = channel;
      unSubscribe();
      disconnect();
    };
  }, []);

  useEffect(() => {
    if (session?.connected && previousChannel.current) {
      unSubscribe();
      subscribe();
    } else {
      connect();
    }
    return () => {
      unSubscribe();
      disconnect();
    };
  }, [channel]);

  useEffect(() => {
    if (!session) {
      return;
    }

    session.on("connect", subscribe);
    return () => {
      session.off("connect", subscribe);
    };
  }, [session]);

  useEffect(() => {
    if (!session) {
      return;
    }

    session.on("channel_message", onMessage);
    return () => {
      session.off("channel_message", onMessage);
    };
  }, [session, onMessage]);

  useEffect(() => {
    if (!session || !onDisconnect) {
      return;
    }

    session.on("disconnect", onDisconnect);
    return () => {
      session.off("disconnect", onDisconnect);
    };
  }, [session, onDisconnect]);

  const connect = useCallback(async () => {
    const token = await dmnIdentity.getAccessToken();
    const environmentVars = getEnvironmentProps(token);
    const wsToken = token;

    const socketArgs = {
      query: {
        token: wsToken
      },
      reconnectionAttempts: 3,
      secure: true,
      withCredentials: true
    };

    try {
      if (!environmentVars?.WEB_SOCKET_URL) {
        console.warn("No websocket to connect to");
        return;
      }
      const websocket = io(`${environmentVars?.WEB_SOCKET_URL}`, socketArgs);
      setSession(websocket);
    } catch {
      console.warn("Error connecting to websocket");
    }
  }, []);

  const disconnect = () => {
    session?.close();
  };

  const subscribe = async () => {
    if (!session?.connected || channel === "") {
      previousChannel.current = "";
      return;
    }

    if (!session) {
      console.warn("WebSocket NotConnected");
    }

    const token = await dmnIdentity.getAccessToken();

    session.emit("subscribeToChannel", { channel, token }, (response: any) => {
      switch (response.status) {
        case 200:
          previousChannel.current = channel;
          break;
        case 401:
          console.warn("websocket Unauthenticated");
          break;
        case 403:
          console.warn("websocket Unauthorised");
          break;
        default:
          console.warn(`websocket error. Status code: ${response.status}`);
      }
    });
  };

  const unSubscribe = async () => {
    if (!session || !previousChannel.current) {
      return;
    }

    const token = await dmnIdentity.getAccessToken();

    session.emit("unsubscribeFromChannel", {
      channel: previousChannel.current,
      token
    });

    previousChannel.current = channel;
  };
};
